// A retail park drawn on a plot: the row of boxes, the yard behind it, the
// car park in front, the pads on the road, the entrances.
//
// Everything is laid out in two kinds of frame. The street frame has s along
// the street edge and t inward from it: the car park, the car entrance, the
// pads and the trimming of the row are measured in it. Each wing has its own
// frame along the plot edge it backs onto (the rear, or a side): the yard is
// the strip from that edge to the back facade, the boxes the next `depth`
// metres, and the units are cut across it.
//
// The row is built as long as the envelope allows and then trimmed, from the
// street ends of the side wings first and then from the free end of the
// rear wing, until what is left needs no more parking than the plot holds,
// and fits POT, CUT and the green share. That is the trade a retail park
// makes: every square metre of shop takes about one of car park.
import { area, clean, difference, distanceTo, edges, grow, inset, intersection, strip, union, type MultiPoly, type Pt } from "../geom.ts";
import { edgeKinds, type Envelope } from "../envelope.ts";
import { AISLE, packStalls, STALL } from "../parking.ts";
import type { EdgeKind, Site } from "../rules.ts";
import { DOCK_APRON } from "../../rules/ro.ts";
import {
  CAR_ACCESS, DOCK_WIDTH, DRIVE_THRU, GLA_RATIO, MIN_UNIT_WIDTH, SERVICE_DOOR, TRUCK_LANE,
  type RetailKind, type RetailProgramme,
} from "./programme.ts";

// ---------- frames ----------

export interface Frame { a: Pt; u: Pt; n: Pt }
export function frameOf(a: Pt, b: Pt): Frame {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  const u: Pt = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
  // the ring is counter-clockwise, so the left normal points into the plot
  return { a, u, n: [-u[1], u[0]] };
}
export const at = (f: Frame, s: number, t: number): Pt => [f.a[0] + f.u[0] * s + f.n[0] * t, f.a[1] + f.u[1] * s + f.n[1] * t];
export const rect = (f: Frame, s0: number, s1: number, t0: number, t1: number): MultiPoly => [[[at(f, s0, t0), at(f, s1, t0), at(f, s1, t1), at(f, s0, t1)]]];
export const toLocal = (f: Frame, p: Pt): Pt => { const dx = p[0] - f.a[0], dy = p[1] - f.a[1]; return [dx * f.u[0] + dy * f.u[1], dx * f.n[0] + dy * f.n[1]]; };
export function extent(f: Frame, mp: MultiPoly) {
  const pts = mp.flatMap((p) => p[0]).map((p) => toLocal(f, p));
  if (!pts.length) return { s0: 0, s1: 0, t0: 0, t1: 0 };
  return { s0: Math.min(...pts.map((p) => p[0])), s1: Math.max(...pts.map((p) => p[0])), t0: Math.min(...pts.map((p) => p[1])), t1: Math.max(...pts.map((p) => p[1])) };
}
const len = ([a, b]: [Pt, Pt]) => Math.hypot(b[0] - a[0], b[1] - a[1]);
export const big = (mp: MultiPoly, min: number) => mp.filter((p) => area([p]) >= min);
export const centroidOf = (mp: MultiPoly): Pt => {
  const pts = mp.flatMap((p) => p[0]);
  return pts.length ? [pts.reduce((t, p) => t + p[0], 0) / pts.length, pts.reduce((t, p) => t + p[1], 0) / pts.length] : [0, 0];
};

// ---------- what the scheme is made of ----------

export type RetailTypology = "line" | "L-left" | "L-right" | "U";
export type Role = "rear" | "left" | "right";

export interface Spine { edge: number; role: Role; frame: Frame; back: number }

/** A door in the back facade: a loading dock a lorry reverses into, or a service and escape door. */
export interface ServiceOpening {
  a: Pt; b: Pt;
  kind: "dock" | "door";
  /** Outward, into the yard. */
  normal: Pt;
  /** Free paved depth in front of it, m: to the boundary or the next building. */
  apron: number;
  /** The largest vehicle it is drawn for. */
  vehicle: keyof typeof DOCK_APRON;
}

export interface RetailUnit {
  id: string;
  kind: RetailKind;
  /** Index into the scheme's spines. */
  wing: number;
  polygon: MultiPoly;
  area: number;
  gla: number;
  frontage: number;
  depth: number;
  /** The shopfront, along the canopy. */
  front: [Pt, Pt];
  entrance: Pt;
  /** Outward normal of the shopfront, towards the car park. */
  outward: Pt;
  service: ServiceOpening[];
}

export interface Pad {
  id: string;
  building: MultiPoly;
  lot: MultiPoly;
  /** The drive-through lane round the building. */
  lane: MultiPoly;
  stalls: MultiPoly[];
  aisles: MultiPoly;
  entrance: Pt;
  area: number;
  gla: number;
}

export interface SchemeParking {
  stalls: MultiPoly[];
  aisles: MultiPoly;
  /** Spaces given to planting: a tree island every few spaces, and any taken to meet the green share. */
  islands: MultiPoly[];
  /** Spaces turned to planting to reach the green share the PUZ asks for. */
  converted: number;
  /** Indices into `stalls`, nearest the entrances first. */
  accessible: number[];
  ev: number[];
  /** Straight-line walk from each space to the nearest entrance, m. */
  walk: number[];
}

export interface Scheme {
  typology: RetailTypology;
  depth: number;
  /** 1 = the row as long as the envelope allows; less = trimmed for parking, POT, CUT or green. */
  trim: number;
  spines: Spine[];
  wings: MultiPoly[];
  building: MultiPoly;
  units: RetailUnit[];
  pads: Pad[];
  columns: Pt[];
  canopy: MultiPoly;
  frontRoad: MultiPoly;
  service: MultiPoly;
  lanes: MultiPoly;
  access: MultiPoly;
  buffer: MultiPoly;
  parking: SchemeParking;
  green: MultiPoly;
  carAccess: Pt;
  truckGates: Pt[];
  pylon: Pt;
  streetFrame: Frame;
}

// ---------- the plot read for a retail park ----------

export interface Base {
  env: Envelope;
  ring: Pt[];
  kinds: EdgeKind[];
  street: number;
  sf: Frame;
  rear?: number;
  left?: number;
  right?: number;
}

/** Which edge is the street, which the back, which the sides: the row backs onto the rear and the sides. */
export function readPlot(env: Envelope, site: Site): Base {
  const ring = env.plot[0][0];
  const kinds = edgeKinds(site);
  const es = edges(ring);
  const idx = es.map((_, i) => i);
  const streets = idx.filter((i) => kinds[i] === "aliniament");
  const street = (streets.length ? streets : idx).reduce((b, i) => (len(es[i]) > len(es[b]) ? i : b));
  const sf = frameOf(...es[street]);
  const mid = (i: number) => toLocal(sf, [(es[i][0][0] + es[i][1][0]) / 2, (es[i][0][1] + es[i][1][1]) / 2]);
  const others = idx.filter((i) => i !== street && kinds[i] !== "aliniament" && len(es[i]) >= 20);
  const posterior = others.filter((i) => kinds[i] === "posterior");
  // the back: the longest rear edge, or failing that the edge farthest from the street
  const rear = posterior.length
    ? posterior.reduce((b, i) => (len(es[i]) > len(es[b]) ? i : b))
    : others.length ? others.reduce((b, i) => (mid(i)[1] > mid(b)[1] ? i : b)) : undefined;
  // the sides: edges running away from the street, one each side of the plot's middle
  const along = (i: number) => { const [a, b] = es[i]; return Math.abs(((b[0] - a[0]) * sf.n[0] + (b[1] - a[1]) * sf.n[1]) / len(es[i])); };
  const sides = others.filter((i) => i !== rear && len(es[i]) >= 25 && along(i) > 0.6);
  const centre = (Math.min(...ring.map((p) => toLocal(sf, p)[0])) + Math.max(...ring.map((p) => toLocal(sf, p)[0]))) / 2;
  const longest = (xs: number[]) => (xs.length ? xs.reduce((b, i) => (len(es[i]) > len(es[b]) ? i : b)) : undefined);
  return {
    env, ring, kinds, street, sf, rear,
    left: longest(sides.filter((i) => mid(i)[0] < centre)),
    right: longest(sides.filter((i) => mid(i)[0] >= centre)),
  };
}

export function typologies(b: Base): RetailTypology[] {
  if (b.rear === undefined) return [];
  const out: RetailTypology[] = ["line"];
  if (b.left !== undefined) out.push("L-left");
  if (b.right !== undefined) out.push("L-right");
  if (b.left !== undefined && b.right !== undefined) out.push("U");
  return out;
}

// ---------- one typology at one depth, before trimming ----------

export interface Prepared {
  b: Base;
  prog: RetailProgramme;
  typology: RetailTypology;
  depth: number;
  spines: Spine[];
  full: MultiPoly[];
  /** Each spine's whole yard, boundary to back facade. */
  yards: MultiPoly[];
  /** All of them: the most yard any trim needs. */
  service: MultiPoly;
  lanes: MultiPoly;
  streetZone: MultiPoly;
  access: MultiPoly;
  sAccess: number;
  pads: Pad[];
  /** Where stalls may go before the row, its yard, its canopy and its front road are taken out. */
  region: MultiPoly;
  rearFront: number;
  sideFrom: number;
  rearS: [number, number];
}

export function prepare(b: Base, prog: RetailProgramme, typology: RetailTypology, depth: number): Prepared | null {
  const { env, ring, sf } = b;
  const plot = env.plot;
  const ground = env.storeys[0];
  if (!ground || b.rear === undefined) return null;
  const es = edges(ring);
  const roles: [number, Role][] = [[b.rear, "rear"]];
  if ((typology === "L-left" || typology === "U") && b.left !== undefined) roles.push([b.left, "left"]);
  if ((typology === "L-right" || typology === "U") && b.right !== undefined) roles.push([b.right, "right"]);
  const spines: Spine[] = roles.map(([edge, role]) => ({ edge, role, frame: frameOf(...es[edge]), back: Math.max(ground.setbacks[edge], prog.serviceDepth) }));

  const yards = spines.map((sp) => intersection(plot, strip(...es[sp.edge], 0, sp.back)));
  // A single row needs a lorry lane down one side to reach its yard; an L or
  // a U reaches the street along the yard of its side wing.
  const service = clean(union(...yards));
  // The yards must be one piece that reaches the street. A single row needs
  // a lorry lane down one side for that; an L or a U usually reaches it
  // along its side wing's yard. Where they do not (a side that does not meet
  // the street), lanes are tried along the edges next to the street, and a
  // typology whose yard still cannot be reached is not offered.
  const gateZone = union(...es.map((e, i) => (b.kinds[i] === "aliniament" ? strip(...e, -1, prog.streetBuffer + 2) : [])));
  const laneOf = (i: number) => intersection(plot, strip(...es[i], 0, TRUCK_LANE));
  const reaches = (x: MultiPoly) => { const u = big(union(x), 10); return u.length === 1 && area(intersection(u, gateZone)) > 10; };
  let lanes: MultiPoly = typology === "line" && (b.right ?? b.left) !== undefined ? laneOf((b.right ?? b.left)!) : [];
  if (!reaches(union(service, lanes))) {
    const n = es.length;
    const near = es.map((_, i) => i).filter((i) => b.kinds[i] !== "aliniament" && !spines.some((sp) => sp.edge === i))
      .sort((x, y) => Math.min((x - b.street + n) % n, (b.street - x + n) % n) - Math.min((y - b.street + n) % n, (b.street - y + n) % n));
    const fix = near.find((i) => reaches(union(service, lanes, laneOf(i))));
    if (fix === undefined) return null;
    lanes = clean(union(lanes, laneOf(fix)));
  }
  const blocked = union(service, lanes);
  const raw = spines.map((sp) => intersection(ground.footprint, strip(...es[sp.edge], sp.back, sp.back + depth)));
  const rearFull = big(clean(difference(raw[0], blocked)), 50);
  // the rear wing keeps the corners; a side wing starts where the rear wing's front is
  // On a narrow plot the two side wings of a U reach each other: the first one keeps the overlap.
  const full: MultiPoly[] = [rearFull];
  for (const w of raw.slice(1)) full.push(big(clean(difference(w, blocked, ...full)), 50));
  if (area(rearFull) < 300) return null;

  const streetZone = union(...es.map((e, i) => (b.kinds[i] === "aliniament" ? intersection(plot, strip(...e, 0, prog.streetBuffer)) : [])));
  const rearExt = extent(sf, rearFull);
  const sides = union(...full.slice(1));
  const plotExt = extent(sf, plot);

  // the car entrance: on the street, opposite the middle of the row, clear of the lorry lanes
  const streetLen = len(es[b.street]);
  let sAccess = Math.max(12, Math.min(streetLen - 12, (rearExt.s0 + rearExt.s1) / 2));
  const lorries = extent(sf, intersection(union(lanes, ...yards.slice(1)), strip(...es[b.street], 0, prog.streetBuffer + 1)));
  if (lanes.length || yards.length > 1) {
    if (sAccess + CAR_ACCESS / 2 + 20 > lorries.s0 && sAccess - CAR_ACCESS / 2 - 20 < lorries.s1) {
      const leftRoom = lorries.s0 - 20 - CAR_ACCESS / 2, rightRoom = lorries.s1 + 20 + CAR_ACCESS / 2;
      sAccess = leftRoom - 12 > 0 && Math.abs(leftRoom - sAccess) <= Math.abs(rightRoom - sAccess) ? leftRoom : rightRoom < streetLen - 12 ? rightRoom : sAccess;
    }
  }
  const accessEnd = Math.max(prog.streetBuffer + 15, rearExt.t0 - prog.canopyDepth);
  // slivers the wings leave of the entrance road (under 2 m wide) are not road
  const access = big(clean(grow(grow(difference(intersection(plot, rect(sf, sAccess - CAR_ACCESS / 2, sAccess + CAR_ACCESS / 2, -2, accessEnd)), union(...full), service, lanes), -1), 1)), 20);

  const p: Prepared = {
    b, prog, typology, depth, spines, full, yards, service, lanes, streetZone, access, sAccess, pads: [], region: [],
    rearFront: rearExt.t0, sideFrom: full.length > 1 ? extent(sf, sides).t0 : rearExt.t0, rearS: [rearExt.s0, rearExt.s1],
  };
  p.pads = placePads(p, plotExt);
  p.region = big(clean(difference(inset(plot, 1.5), streetZone, lanes, grow(access, 0.5), ...p.pads.map((x) => grow(x.lot, 1)))), 30);
  return p;
}

/** Drive-through pads on the frontage, flanking the car entrance, each on a lot with its own lane and spaces. */
function placePads(p: Prepared, plotExt: ReturnType<typeof extent>): Pad[] {
  const { prog, b } = p;
  if (prog.pads <= 0 || prog.padArea <= 0) return [];
  const sf = b.sf, plot = b.env.plot;
  const bd = 16, bw = Math.max(14, prog.padArea / bd);
  const lotW = bw + 2 * DRIVE_THRU + 12, lotD = bd + 6 + DRIVE_THRU + 2 * STALL.length + AISLE;
  const t0 = prog.streetBuffer;
  const front = frontOf(p, p.full);
  const allowed = difference(inset(plot, 1.5), p.streetZone, p.service, p.lanes, grow(p.access, 3), grow(union(...p.full, front.canopy), 4), grow(front.frontRoad, 2));
  const cands: { s: number; lot: MultiPoly; building: MultiPoly; sc: number }[] = [];
  for (let s = plotExt.s0; s + lotW <= plotExt.s1; s += 2) {
    const lot = rect(sf, s, s + lotW, t0, t0 + lotD);
    if (area(difference(lot, allowed)) > 1) continue;
    const sc = s + lotW / 2;
    const building = rect(sf, sc - bw / 2, sc + bw / 2, t0 + 6, t0 + 6 + bd);
    if (area(difference(building, b.env.storeys[0].footprint)) > 0.5) continue;
    cands.push({ s, lot, building, sc });
  }
  cands.sort((x, y) => Math.abs(x.sc - p.sAccess) - Math.abs(y.sc - p.sAccess));
  const chosen: typeof cands = [];
  for (const c of cands) {
    if (chosen.length >= prog.pads) break;
    if (chosen.some((o) => c.s < o.s + lotW + 4 && o.s < c.s + lotW + 4)) continue;
    chosen.push(c);
  }
  chosen.sort((x, y) => x.s - y.s);
  return chosen.map((c, i) => {
    const lane = intersection(difference(grow(c.building, DRIVE_THRU), c.building), c.lot);
    const free = inset(difference(c.lot, grow(c.building, DRIVE_THRU + 0.5)), 0.3);
    const pk = packStalls(free, [], sf.a, at(sf, 1, 0));
    const a = area(c.building);
    return {
      id: `PAD${i + 1}`, building: c.building, lot: c.lot, lane, stalls: pk.stalls,
      aisles: intersection(pk.aisles, grow(union(...pk.stalls), AISLE)),
      entrance: at(sf, c.sc, t0 + 6 + bd), area: a, gla: a * GLA_RATIO,
    };
  });
}

// ---------- trimming ----------

/**
 * The wings at trim k. From 1 down to ½ the side wings are cut back from
 * the street towards the rear wing; below ½ they are gone and the rear wing
 * shortens, from its free end in an L and from both ends otherwise, down to
 * 30 m. A row with no side wings uses the whole range on the rear wing.
 */
export function trimmed(p: Prepared, k: number): MultiPoly[] {
  const sf = p.b.sf;
  const sides = p.full.length > 1 && p.full.slice(1).some((w) => w.length);
  const f = sides ? Math.min(1, k / 0.5) : k;
  const [r0, r1] = p.rearS;
  const L = r1 - r0, minLen = Math.min(L, 30), keep = minLen + f * (L - minLen);
  const win: [number, number] = f >= 1 ? [r0 - 1, r1 + 1]
    : p.typology === "L-left" ? [r0 - 1, r0 + keep] : p.typology === "L-right" ? [r1 - keep, r1 + 1] : [(r0 + r1) / 2 - keep / 2, (r0 + r1) / 2 + keep / 2];
  const rear = f >= 1 ? p.full[0] : big(clean(intersection(p.full[0], rect(sf, win[0], win[1], -1e4, 1e4))), 50);
  const cut = k >= 0.5 ? p.rearFront - (p.rearFront - p.sideFrom) * ((k - 0.5) / 0.5) : Infinity;
  const wings = [rear, ...p.full.slice(1).map((w) => {
    if (!w.length || cut === Infinity) return [];
    const kept = k >= 1 ? w : big(clean(intersection(w, rect(sf, -1e4, 1e4, cut, 1e4))), 50);
    return area(kept) < 300 ? [] : kept;
  })];
  // a wing shorter along its edge than the narrowest unit holds no shop
  return wings.map((w, i) => { if (!w.length) return w; const e = extent(p.spines[i].frame, w); return e.s1 - e.s0 < MIN_UNIT_WIDTH ? [] : w; });
}

/** Yard kept beyond the end of a wing, m: room for a lorry to turn at the last dock. */
export const TURN = 15;

/**
 * The yards a trimmed row needs: the full depth behind each wing that is
 * still there and a turning length past its ends, and along every spine
 * edge a lorry lane to the street. A side wing trimmed away gives its yard
 * back to the car park and keeps only the lane.
 */
export function serviceFor(p: Prepared, wings: MultiPoly[]): MultiPoly {
  const es = edges(p.b.ring);
  const plot = p.b.env.plot;
  return clean(union(...p.spines.map((sp, i) => {
    const lane = intersection(plot, strip(...es[sp.edge], 0, Math.min(TRUCK_LANE, sp.back)));
    if (!wings[i].length) return lane;
    const e = extent(sp.frame, wings[i]);
    return union(lane, intersection(p.yards[i], rect(sp.frame, e.s0 - TURN, e.s1 + TURN, -1, sp.back + 0.01)));
  })));
}

/** Canopy and drive lane along each wing's shopfront, and the yards behind. */
export function frontOf(p: Prepared, wings: MultiPoly[]): { building: MultiPoly; canopy: MultiPoly; frontRoad: MultiPoly; service: MultiPoly } {
  const building = clean(union(...wings));
  const service = serviceFor(p, wings);
  const canopies: MultiPoly[] = [], roads: MultiPoly[] = [];
  wings.forEach((w, i) => {
    if (!w.length) return;
    const f = p.spines[i].frame, e = extent(f, w);
    canopies.push(rect(f, e.s0, e.s1, e.t1, e.t1 + p.prog.canopyDepth));
    roads.push(rect(f, e.s0, e.s1, e.t1 + p.prog.canopyDepth, e.t1 + p.prog.canopyDepth + p.prog.frontRoad));
  });
  const plot = p.b.env.plot;
  // where a side wing's canopy runs into a yard or across the car entrance, the road wins
  const canopy = big(clean(difference(intersection(union(...canopies), plot), building, service, p.lanes, p.access)), 1);
  const frontRoad = big(clean(difference(intersection(union(...roads), plot), building, canopy, service, p.lanes)), 5);
  return { building, canopy, frontRoad, service };
}

export function parkingRegion(p: Prepared, building: MultiPoly, canopy: MultiPoly, frontRoad: MultiPoly, service: MultiPoly): MultiPoly {
  return big(clean(difference(p.region, service, grow(union(building, canopy), 0.5), frontRoad)), 30);
}

export type Rows = "along" | "across";
export interface Packed { stalls: MultiPoly[]; aisles: MultiPoly; frame: Frame; rows: Rows }

/**
 * The better of rows along the street and rows square to it. `quick` tries
 * two offsets of the row pattern instead of eight, for estimates; `rows`
 * fixes the direction once an estimate has chosen it.
 */
export function packRegion(p: Prepared, region: MultiPoly, opt: { quick?: boolean; rows?: Rows } = {}): Packed {
  const sf = p.b.sf;
  // a subset of the full offsets, so a quick packing never finds more spaces than the full one
  const offsets = opt.quick ? { dt: [0, 4], ds: [0] } : undefined;
  const try_ = (rows: Rows): Packed => {
    const frame = rows === "along" ? sf : frameOf(sf.a, at(sf, 0, 1));
    // the entrance road is out of the region already; as an obstacle it gets the exact test a thin piece of it needs
    return { ...packStalls(region, p.access, frame.a, at(frame, 1, 0), offsets), frame, rows };
  };
  const along = opt.rows !== "across" ? try_("along") : null;
  const across = opt.rows !== "along" ? try_("across") : null;
  const best = !along ? across! : !across ? along : across.stalls.length >= along.stalls.length ? across : along;
  // aisles that no stall opens onto are not paved
  return { ...best, aisles: best.stalls.length ? intersection(best.aisles, grow(union(...best.stalls), AISLE)) : [] };
}

// ---------- units ----------

interface Planned { kind: RetailKind; width: number }

/** The mix as a list of units, as many as the row's length holds, widths at the row's depth. */
export function planUnits(prog: RetailProgramme, totalLength: number, depth: number): Planned[] {
  const mix = prog.mix.filter((m) => m.share > 0 && m.area > 0);
  if (!mix.length || totalLength < MIN_UNIT_WIDTH) return [];
  const shares = mix.reduce((t, m) => t + m.share, 0);
  const width = (a: number) => Math.max(MIN_UNIT_WIDTH, a / (depth * GLA_RATIO));
  const gla = totalLength * depth * GLA_RATIO;
  const counts = mix.map((m) => Math.round(((m.share / shares) * gla) / m.area));
  const total = () => counts.reduce((t, c, i) => t + c * width(mix[i].area), 0);
  // Too long a row: add the type furthest below its share; too short: take a
  // unit off the type furthest above it. A row is stretched or squeezed by at
  // most a fifth, so a "1,000 m²" box stays near 1,000 m².
  const off = (i: number) => (counts[i] * width(mix[i].area)) / Math.max(1, total()) - mix[i].share / shares;
  for (let guard = 0; guard < 400; guard++) {
    const scale = totalLength / Math.max(1, total());
    if (scale > 1.2) {
      const i = mix.map((_, j) => j).reduce((b, j) => (off(j) < off(b) ? j : b));
      if (width(mix[i].area) > totalLength - total()) { const small = mix.map((_, j) => j).reduce((b, j) => (mix[j].area < mix[b].area ? j : b)); counts[small]++; } else counts[i]++;
    } else if (scale < 0.85 && counts.reduce((t, c) => t + c, 0) > 1) {
      const i = mix.map((_, j) => j).filter((j) => counts[j] > 0).reduce((b, j) => (off(j) > off(b) ? j : b));
      counts[i]--;
    } else break;
  }
  if (!counts.some((c) => c > 0)) counts[mix.map((_, j) => j).reduce((b, j) => (mix[j].area < mix[b].area ? j : b))] = 1;
  const list: Planned[] = mix.flatMap((m, i) => Array.from({ length: counts[i] }, () => ({ kind: m.kind, width: width(m.area) })));
  // Large boxes at the two ends, small units between them: the anchors pull
  // shoppers past every shop on the way.
  list.sort((x, y) => y.width - x.width);
  const head: Planned[] = [], tail: Planned[] = [];
  list.forEach((u, i) => (i % 2 ? tail : head).push(u));
  return [...head, ...tail.reverse()];
}

/** Free paved depth in front of a point along a direction: to the boundary or a building. */
export function apron(from: Pt, dir: Pt, plot: MultiPoly, buildings: MultiPoly): number {
  const free = (d: number) => { const q: Pt = [from[0] + dir[0] * d, from[1] + dir[1] * d]; return distanceTo(q, plot) === 0 && distanceTo(q, buildings) > 0; };
  let d = 0.5;
  while (d <= 60 && free(d)) d += 0.5;
  if (d > 60) return 60;
  // to the centimetre: a 30 m yard should read 30 m, not 29.5
  let lo = d - 0.5, hi = d;
  while (hi - lo > 0.01) { const m = (lo + hi) / 2; if (free(m)) lo = m; else hi = m; }
  return lo;
}

/**
 * Cut the wings into units. The run goes down the left side wing from the
 * street, along the rear wing, and back up the right side wing; the planned
 * units are dealt along it in order, each wing taking as many as fit, then
 * stretched to its length exactly. Party walls snap to half-bays where that
 * leaves both units their minimum width.
 */
export function layUnits(p: Prepared, wings: MultiPoly[], building: MultiPoly): { units: RetailUnit[]; columns: Pt[] } {
  const { prog, b } = p;
  const sf = b.sf;
  const order = p.spines.map((sp, i) => ({ sp, i })).filter(({ i }) => wings[i].length)
    .sort((x, y) => ["left", "rear", "right"].indexOf(x.sp.role) - ["left", "rear", "right"].indexOf(y.sp.role));
  const runs = order.map(({ sp, i }) => {
    const f = sp.frame, e = extent(f, wings[i]);
    const dot = (v: Pt, w: Pt) => v[0] * w[0] + v[1] * w[1];
    const reversed = sp.role === "rear" ? dot(f.u, sf.u) < 0 : sp.role === "left" ? dot(f.u, sf.n) < 0 : dot(f.u, sf.n) > 0;
    return { sp, i, f, e, reversed, length: e.s1 - e.s0, depth: e.t1 - sp.back };
  }).filter((r) => r.length >= MIN_UNIT_WIDTH);
  const total = runs.reduce((t, r) => t + r.length, 0);
  const depth = runs.length ? runs.reduce((t, r) => t + r.depth * r.length, 0) / Math.max(1, total) : p.depth;
  const planned = planUnits(prog, total, depth);
  const scale = total / Math.max(1, planned.reduce((t, u) => t + u.width, 0));
  const units: RetailUnit[] = [];
  const columns: Pt[] = [];
  let k = 0;
  runs.forEach((r, ri) => {
    const mine: Planned[] = [];
    let used = 0;
    while (k < planned.length) {
      const w = planned[k].width * scale;
      // leave at least one unit for each wing still to come
      if (ri < runs.length - 1 && mine.length && (used + w / 2 > r.length || planned.length - k <= runs.length - 1 - ri)) break;
      mine.push(planned[k++]); used += w;
    }
    // a wing the plan ran out for still gets a unit: the smallest type, its whole length
    if (!mine.length) mine.push({ kind: planned.length ? planned.reduce((x, y) => (y.width < x.width ? y : x)).kind : "small", width: r.length });
    let fit = r.length / mine.reduce((t, u) => t + u.width, 0);
    // a squeeze that would take a unit under its minimum width takes the smallest unit out instead
    while (mine.length > 1 && mine.some((u) => u.width * fit < MIN_UNIT_WIDTH - 1e-6)) {
      mine.splice(mine.reduce((b, u, j) => (u.width < mine[b].width ? j : b), 0), 1);
      fit = r.length / mine.reduce((t, u) => t + u.width, 0);
    }
    const half = prog.bay / 2;
    // boundaries along the run, then snapped to the structural module
    const cuts = [0];
    for (const u of mine) cuts.push(cuts[cuts.length - 1] + u.width * fit);
    cuts[cuts.length - 1] = r.length;
    for (let j = 1; j < cuts.length - 1; j++) {
      const snapped = Math.round(cuts[j] / half) * half;
      if (snapped - cuts[j - 1] >= MIN_UNIT_WIDTH && cuts[j + 1] - snapped >= MIN_UNIT_WIDTH) cuts[j] = snapped;
    }
    const sOf = (x: number) => (r.reversed ? r.e.s1 - x : r.e.s0 + x);
    const { f, sp } = r;
    const tB = sp.back, tF = r.e.t1;
    // a piece the plot's outline leaves under 60 m² is not a shop: it joins its neighbour
    const pieces = mine.map((u, j) => ({ kind: u.kind, x0: cuts[j], x1: cuts[j + 1] }));
    const cut = (x0: number, x1: number) => {
      const [sa, sb] = [sOf(x0), sOf(x1)].sort((x, y) => x - y);
      return { sa, sb, poly: intersection(wings[r.i], rect(f, sa, sb, tB - 1, tF + 1)) };
    };
    for (let j = 0; j < pieces.length && pieces.length > 1; j++) {
      if (area(cut(pieces[j].x0, pieces[j].x1).poly) >= 60) continue;
      const into = j + 1 < pieces.length ? j + 1 : j - 1;
      pieces[into].x0 = Math.min(pieces[into].x0, pieces[j].x0); pieces[into].x1 = Math.max(pieces[into].x1, pieces[j].x1);
      pieces.splice(j, 1); j = -1;
    }
    const out: Pt = [-f.n[0], -f.n[1]];
    for (const pc of pieces) {
      const { sa, sb, poly } = cut(pc.x0, pc.x1);
      if (area(poly) < 60) continue;
      const mid = (sa + sb) / 2;
      const vehicle = pc.kind === "anchor" ? "articulated" : pc.kind === "medium" ? "rigid" : "van";
      const offsets = pc.kind === "anchor" ? [-(DOCK_WIDTH / 2 + 1), DOCK_WIDTH / 2 + 1] : pc.kind === "medium" ? [0] : [];
      const opening = (c: number, w: number, kind: "dock" | "door", v: ServiceOpening["vehicle"]): ServiceOpening => {
        const q0 = at(f, c - w / 2, tB), q1 = at(f, c + w / 2, tB);
        // measured from 5 cm out, clear of the wall itself, and the 5 cm given back
        return { a: q0, b: q1, kind, normal: out, vehicle: v, apron: apron(at(f, c, tB - 0.05), out, b.env.plot, building) + 0.05 };
      };
      // Docks in the middle of the back wall, unless a corner of the plot
      // cuts the yard short there: then where the yard is deepest.
      const docksAt = (c: number) => offsets.map((d) => opening(c + d, DOCK_WIDTH, "dock", vehicle));
      let docks = docksAt(mid);
      const shortest = (ds: ServiceOpening[]) => Math.min(...ds.map((o) => o.apron));
      if (docks.length && shortest(docks) < DOCK_APRON[vehicle] - 0.1) {
        const reach = Math.max(...offsets.map(Math.abs)) + DOCK_WIDTH / 2 + 0.5;
        for (let c = sa + reach; c <= sb - reach; c += 2) {
          const ds = docksAt(c);
          if (shortest(ds) > shortest(docks) + 0.05) docks = ds;
        }
      }
      const dc = docks.length ? toLocal(f, [(docks[0].a[0] + docks[docks.length - 1].b[0]) / 2, (docks[0].a[1] + docks[docks.length - 1].b[1]) / 2])[0] : mid;
      // every unit has a service and escape door at the back, beside its docks
      const door = docks.length ? (dc + DOCK_WIDTH * 1.5 + 2 <= sb - 1.5 ? dc + DOCK_WIDTH * 1.5 + 2 : dc - DOCK_WIDTH * 1.5 - 2) : mid;
      const service = [...docks, opening(Math.max(sa + 1.5, Math.min(sb - 1.5, door)), SERVICE_DOOR, "door", docks.length ? vehicle : "van")];
      const ar = area(poly);
      units.push({
        id: "", kind: pc.kind, wing: r.i, polygon: poly, area: ar, gla: ar * GLA_RATIO, frontage: sb - sa, depth: tF - tB,
        front: [at(f, sa + 0.6, tF), at(f, sb - 0.6, tF)], entrance: at(f, mid, tF), outward: f.n, service,
      });
    }
    // the structural grid: bays along the row, spans across it no wider than 25 m
    const spans = Math.max(1, Math.ceil(r.depth / 25));
    for (let s = r.e.s0; s <= r.e.s1 + 0.01; s += prog.bay) for (let j = 0; j <= spans; j++) {
      const q = at(f, Math.min(s, r.e.s1), tB + (j * r.depth) / spans);
      if (distanceTo(q, wings[r.i]) < 0.05) columns.push(q);
    }
  });
  units.forEach((u, i) => (u.id = `U${String(i + 1).padStart(2, "0")}`));
  return { units, columns };
}

// ---------- the car park, finished ----------

/**
 * Tree islands every few spaces in each row, then, if the PUZ asks for more
 * green than is left, the spaces farthest from any entrance become planting
 * too, as long as the required number of spaces stays.
 */
export function finishParking(
  packed: { stalls: MultiPoly[]; aisles: MultiPoly; frame: Frame }, entrances: Pt[], treeEvery: number,
  required: number, padStalls: number, greenShort: number,
): SchemeParking {
  const centre = (st: MultiPoly): Pt => centroidOf(st);
  const islands: MultiPoly[] = [];
  let stalls = packed.stalls;
  if (treeEvery > 0) {
    const rows = new Map<number, { st: MultiPoly; s: number }[]>();
    for (const st of stalls) {
      const [s, t] = toLocal(packed.frame, centre(st));
      const key = Math.round(t * 2);
      (rows.get(key) ?? rows.set(key, []).get(key)!).push({ st, s });
    }
    const keep: MultiPoly[] = [];
    for (const row of rows.values()) {
      row.sort((x, y) => x.s - y.s);
      let run = 0;
      row.forEach((x, i) => {
        if (i > 0 && x.s - row[i - 1].s > STALL.width + 0.1) run = 0;
        run++;
        if (run % (treeEvery + 1) === 0) islands.push(x.st); else keep.push(x.st);
      });
    }
    stalls = keep;
  }
  const dist = (st: MultiPoly) => { const c = centre(st); return entrances.length ? Math.min(...entrances.map((e) => Math.hypot(e[0] - c[0], e[1] - c[1]))) : 0; };
  const byDist = stalls.map((st) => ({ st, d: dist(st) })).sort((x, y) => x.d - y.d);
  let converted = 0;
  // the tree islands already gave some of the green back
  let short = greenShort - islands.length * STALL.width * STALL.length;
  while (short > 0 && byDist.length + padStalls > required) {
    islands.push(byDist.pop()!.st);
    converted++;
    short -= STALL.width * STALL.length;
  }
  const cap = byDist.length + padStalls;
  const nAcc = cap ? Math.max(1, Math.ceil(cap * 0.04)) : 0;
  const nEv = Math.ceil(cap * 0.2);
  return {
    stalls: byDist.map((x) => x.st), aisles: packed.aisles, islands, converted,
    accessible: byDist.slice(0, nAcc).map((_, i) => i),
    ev: byDist.slice(nAcc, nAcc + nEv).map((_, i) => nAcc + i),
    walk: byDist.map((x) => x.d),
  };
}

/** Where lorries come in: each yard or lane where it meets the street strip. */
export function truckGates(p: Prepared, service: MultiPoly): Pt[] {
  const es = edges(p.b.ring);
  const zone = union(...es.map((e, i) => (p.b.kinds[i] === "aliniament" ? strip(...e, -1, p.prog.streetBuffer + 2) : [])));
  const pieces = intersection(union(service, p.lanes), zone);
  return pieces.filter((q) => area([q]) > 10).map((q) => {
    const c = centroidOf([q]);
    const [s] = toLocal(p.b.sf, c);
    return at(p.b.sf, s, 0);
  });
}
